import { gql } from '@apollo/client';
import {
  IMAGE_FIELDS,
  MEDIA_FIELDS,
  MONEY_FIELDS,
  POINT_OF_VIEW_3D_FIELDS,
  TRANSLATED_STRING_FIELDS
} from '../fragments/common';
import { DESIGNER_THEME_BASIC_PROFILE_FIELDS } from '../fragments/common';
import { ACCESSORY_CONFIGURATION_FIELDS, ACCESSORY_REFERENCE_FIELDS } from './accessory';
import { ENGRAVING_FIELDS } from './engraving';
import { JOB_SUMMARY_FIELDS } from './job';
import { PIECE_CONFIGURATION_FIELDS, PIECE_REFERENCE_FIELDS } from './piece';
import { PRICING_CATALOG_FIELDS } from './pricing';

// --------------------------------------------------------- Merchant Channel ---------------------------------------------------------
export const MERCHANT_CHANNEL_SYNCHRONISED_WITH_SHOP_STATUS_FIELDS = gql`
  ${JOB_SUMMARY_FIELDS}
  fragment MerchantChannelSynchronisedWithShopStatusFields on MerchantChannelSynchronisedWithShopStatus {
    platformDataLastUpdated
    lastSynchronisationJob {
      ...JobSummaryFields
    }
  }
`;

const VIEWER_3D_ENVIRONMENT_FIELDS = gql`
  ${POINT_OF_VIEW_3D_FIELDS}
  ${MEDIA_FIELDS}
  fragment Viewer3DEnvironmentFields on Viewer3DEnvironment {
    defaultPointOfView3D {
      ...PointOfView3DFields
    }
    hdri {
      ...MediaFields
    }
  }
`;

const MERCHANT_CHANNEL_CONFIGURATION_FIELDS = gql`
  ${DESIGNER_THEME_BASIC_PROFILE_FIELDS}
  ${VIEWER_3D_ENVIRONMENT_FIELDS}
  fragment MerchantChannelConfigurationFields on MerchantChannelConfiguration {
    primaryWeightUnit
    primaryCurrency
    defaultLanguage
    availableLanguages
    theme {
      ...DesignerThemeBasicProfileFields
    }
    viewer3DEnvironment {
      ...Viewer3DEnvironmentFields
    }
  }
`;

export const MERCHANT_CHANNEL_FIELDS = gql`
  ${MERCHANT_CHANNEL_CONFIGURATION_FIELDS}
  fragment MerchantChannelFields on MerchantChannel {
    id
    active
    partyId
    name
    domain
    channelType
    configuration {
      ...MerchantChannelConfigurationFields
    }
    defaultChannelCatalog
    createdAt
    updatedAt
  }
`;

// --------------------------------------------------------- Merchant Channel Catalog ---------------------------------------------------------
const TEXT_FEATURE_CONFIGURATION_FIELDS = gql`
  fragment TextFeatureConfigurationFields on TextFeatureConfiguration {
    availableFontIds
    minFontSize
    deltaFontSize
  }
`;

const SINGLE_LETTER_FEATURE_CONFIGURATION_FIELDS = gql`
  fragment SingleLetterFeatureConfigurationFields on SingleLetterFeatureConfiguration {
    availableFontIds
    minFontSize
    deltaFontSize
  }
`;

const SYMBOL_FEATURE_CONFIGURATION_FIELDS = gql`
  fragment SymbolFeatureConfigurationFields on SymbolFeatureConfiguration {
    availableSymbolCollectionIds
    minScalingFactor
    deltaScalingFactor
  }
`;

const DESIGNER_FEATURE_FIELDS = gql`
  ${TEXT_FEATURE_CONFIGURATION_FIELDS}
  ${SINGLE_LETTER_FEATURE_CONFIGURATION_FIELDS}
  ${SYMBOL_FEATURE_CONFIGURATION_FIELDS}
  fragment DesignerFeatureFields on DesignerFeature {
    type
    textFeatureConfiguration {
      ...TextFeatureConfigurationFields
    }
    singleLetterFeatureConfiguration {
      ...SingleLetterFeatureConfigurationFields
    }
    symbolFeatureConfiguration {
      ...SymbolFeatureConfigurationFields
    }
  }
`;

const MERCHANT_CHANNEL_ACCESSORY_VARIANT_FIELDS = gql`
  ${MONEY_FIELDS}
  ${IMAGE_FIELDS}
  ${ACCESSORY_REFERENCE_FIELDS}
  fragment MerchantChannelAccessoryVariantFields on MerchantChannelAccessoryVariant {
    id
    image {
      ...ImageFields
    }
    accessoryReference {
      ...AccessoryReferenceFields
    }
    acquisitionPrice {
      ...MoneyFields
    }
    retailPrice {
      ...MoneyFields
    }
    weightInGrams
  }
`;

const MERCHANT_CHANNEL_PIECE_VARIANT_FIELDS = gql`
  ${ENGRAVING_FIELDS}
  ${MONEY_FIELDS}
  ${IMAGE_FIELDS}
  ${PIECE_REFERENCE_FIELDS}
  fragment MerchantChannelPieceVariantFields on MerchantChannelPieceVariant {
    id
    image {
      ...ImageFields
    }
    pieceReference {
      ...PieceReferenceFields
    }
    engravings {
      ...EngravingFields
    }
    acquisitionPrice {
      ...MoneyFields
    }
    retailPrice {
      ...MoneyFields
    }
    weightInGrams
  }
`;

const MERCHANT_CHANNEL_PRODUCT_FIELDS = gql`
  ${TRANSLATED_STRING_FIELDS}
  ${MERCHANT_CHANNEL_PIECE_VARIANT_FIELDS}
  ${MERCHANT_CHANNEL_ACCESSORY_VARIANT_FIELDS}
  fragment MerchantChannelProductFields on MerchantChannelProduct {
    id
    productIdentity
    title {
      ...TranslatedStringFields
    }
    description {
      ...TranslatedStringFields
    }
    geometryType
    pieceVariants {
      ...MerchantChannelPieceVariantFields
    }
    accessoryVariants {
      ...MerchantChannelAccessoryVariantFields
    }
  }
`;

const DESIGN_SAMPLE_FIELDS = gql`
  ${ENGRAVING_FIELDS}
  fragment DesignSampleFields on DesignSample {
    id
    pieceEngravings {
      ...EngravingFields
    }
  }
`;

const MERCHANT_DESIGN_SAMPLE_STRATEGY_FIELDS = gql`
  ${ENGRAVING_FIELDS}
  fragment MerchantDesignSampleStrategyFields on MerchantDesignSampleStrategy {
    scope
    iteration
  }
`;

const MERCHANT_DESIGN_SAMPLES_FIELDS = gql`
  ${DESIGN_SAMPLE_FIELDS}
  ${MERCHANT_DESIGN_SAMPLE_STRATEGY_FIELDS}
  fragment MerchantDesignSamplesFields on MerchantDesignSamples {
    strategy {
      ...MerchantDesignSampleStrategyFields
    }
    samples {
      ...DesignSampleFields
    }
  }
`;

const MERCHANT_CHANNEL_COLLECTION_FIELDS = gql`
  ${PRICING_CATALOG_FIELDS}
  ${PIECE_CONFIGURATION_FIELDS}
  ${ACCESSORY_CONFIGURATION_FIELDS}
  ${DESIGNER_FEATURE_FIELDS}
  ${MERCHANT_DESIGN_SAMPLES_FIELDS}
  ${MERCHANT_CHANNEL_PRODUCT_FIELDS}
  fragment MerchantChannelCollectionFields on MerchantChannelCollection {
    id
    retailPrices {
      ...PricingCatalogFields
    }
    availablePieceConfigurations {
      ...PieceConfigurationFields
    }
    availableAccessoryConfigurations {
      ...AccessoryConfigurationFields
    }
    availableSymbolCollectionIds
    availableFontIds
    availableAccessoryIds
    availablePieceIds

    availableDesignerFeatures {
      ...DesignerFeatureFields
    }

    samples {
      ...MerchantDesignSamplesFields
    }

    products {
      ...MerchantChannelProductFields
    }
    channelShopReferenceCollection
  }
`;

export const MERCHANT_CHANNEL_CATALOG_FIELDS = gql`
  ${MERCHANT_CHANNEL_COLLECTION_FIELDS}
  fragment MerchantChannelCatalogFields on MerchantChannelCatalog {
    id
    partyId
    channelId
    availableCollections {
      ...MerchantChannelCollectionFields
    }
    createdAt
    updatedAt
  }
`;
